import React from 'react';

import ALink from 'components/basic/ALink';
import Icon, { IconTypes } from 'components/basic/Icon';
import { BodyM, BulletList, ListItem } from 'components/basic/Typography';

import s from './FAQ.module.scss';

export const faqItems = [
  {
    header: 'How do you ensure the anonymity and safety of participants?',
    content: (
      <>
        <BodyM className="mb-5">
          We create anonymous accounts for your participants. This is the safest
          way to depersonalize and anonymize their data. If you need to
          associate these accounts with participants, do so at your end.
        </BodyM>
        <div className={s.faqChecksWrapper}>
          <BodyM>
            <Icon
              className="me-3"
              color="breakfast"
              icon={IconTypes.checkCircleOutline}
            />{' '}
            IRB Approved
          </BodyM>
          <BodyM>
            <Icon
              className="me-3"
              color="breakfast"
              icon={IconTypes.checkCircleOutline}
            />{' '}
            HIPAA Compliant
          </BodyM>
          <BodyM>
            <Icon
              className="me-3"
              color="breakfast"
              icon={IconTypes.checkCircleOutline}
            />{' '}
            Secure Platform
          </BodyM>
        </div>
      </>
    ),
  },
  {
    header: 'What kind of data I can get using Welltory Premium for my research?',
    content: (
      <BodyM>
        You can monitor all calculated data, i.e. the following metrics in
        addition to the basic ones: <br />
        <br />
        ANS Balance + Coherence (%) + HRV Score (%), Mean RR (ms), rMSSD (ms),
        MxDMn (s), pNN50 (%), AMo50 (%/50ms), Moda (ms), Total Power (ms²), HF
        (ms²), LF (ms²), VLF (ms²)
      </BodyM>
    ),
  },
  {
    header:
      'How long does it take from the first meeting to the signing of the contract?',
    content: (
      <BodyM>
        Usually, approvals take several weeks, but we are always happy to speed
        up the process. Once we’ve agreed on terms and payment, it can take us
        up to five working days to create the accounts and transfer them to you.
      </BodyM>
    ),
  },
  {
    header: 'How reliable and scientifically valid is the Welltory data?',
    content: (
      <>
        <BodyM>
          Our research team has designed a professional method of analyzing
          Photoplethysmography (PPG) measurements of heart rate variability
          using a phone camera. Their results have been presented at
          professional conferences and in academic publications.
        </BodyM>
        <br />
        <BodyM>Papers</BodyM>
        <BulletList>
          <ListItem type="bodyM">
            <ALink
              className={s.underline}
              href="https://ieeexplore.ieee.org/document/8856540"
              target="_blank"
            >
              Smartphone PPG: signal processing, quality assessment, and impact
              on HRV parameters
            </ALink>
          </ListItem>
          <ListItem type="bodyM">
            <ALink
              className={s.underline}
              href="https://www.medrxiv.org/content/10.1101/2020.12.23.20247494v1"
              target="_blank"
            >
              Smartphone App Stress Assessments: Heart Rate Variability vs
              Perceived Stress in a Large Group of Adults
            </ALink>
          </ListItem>
          <ListItem type="bodyM">
            <ALink
              className={s.underline}
              href="https://www.sciencedirect.com/science/article/abs/pii/S0169260722000815?via%3Dihub#"
              target="_blank"
            >
              Validity and reliability of different smartphones applications to
              measure HRV during short and ultra-short measurements in elite
              athletes
            </ALink>
          </ListItem>
        </BulletList>
      </>
    ),
  },
  {
    header: 'Are there scientific papers to support the validity of your app?',
    content: (
      <BodyM>
        Welltory analyzes{' '}
        <ALink
          className={s.underline}
          href="https://welltory.com/heart-rate-variability/"
          target="_blank"
        >
          heart rate variability (HRV)
        </ALink>{' '}
        — a health marker backed by over 25,000 studies on PubMed.{' '}
        <ALink
          className={s.underline}
          href="https://www.sciencedirect.com/science/article/abs/pii/S0169260722000815?via%3Dihub#"
          target="_blank"
        >
          It was proved that Welltory is a valid and reliable
        </ALink>{' '}
        smartphone application and can be used to monitor HRV among elite
        endurance athletes. We’ve also published{' '}
        <ALink
          className={s.underline}
          href="https://www.researchgate.net/publication/336327954_Smartphone_PPG_signal_processing_quality_assessment_and_impact_on_HRV_parameters"
          target="_blank"
        >
          research
        </ALink>{' '}
        on our HRV signal processing and quality assessment method. To learn
        more about the science behind Welltory{' '}
        <ALink
          className={s.underline}
          href="https://welltory.com/science/"
          target="_blank"
        >
          visit this page
        </ALink>
        .
      </BodyM>
    ),
  },
  {
    header: 'Do I need additional devices-static for measurements?',
    content: (
      <BodyM>
        No. All you need is a mobile phone with a camera and flash, an installed
        app, and a license to research. <br />
        <br />
        The best and most accurate measurements can be obtained with the Apple
        Watch or BLE heart monitors. However, many studies can be done without
        these tools. We have examples where researchers have collected data from
        a camera measurement because its accuracy{' '}
        <ALink
          className={s.underline}
          href="https://medium.com/welltory/camera-vs-polar-heart-rate-monitor-68902fb6a2d7"
          target="_blank"
        >
          has been proven
        </ALink>
        .
      </BodyM>
    ),
  },
  {
    header: 'How much does it cost to access Welltory Premium for research?',
    content: <BodyM>Please fill out the form to find out</BodyM>,
  },

  {
    header: 'In what format will I get the data?',
    content: (
      <BodyM>
        Look at an example of raw data export{' '}
        <ALink
          className={s.underline}
          href="https://github.com/Welltory/hrv-covid19"
          target="_blank"
        >
          here
        </ALink>
      </BodyM>
    ),
  },
  {
    header: 'Can my study data be used by Welltory or third parties?',
    content: <BodyM>No</BodyM>,
  },
];
